import { useMemo, useState } from "react";

import {
  exceptionsByDate,
  jsDayToMondayFirst,
  templateByDay,
  toIsoDate,
} from "@/features/availability/model/helpers";
import type {
  AvailabilityException,
  TemplateWindow,
} from "@/features/availability/types";
import { DAY_LABELS } from "@/features/availability/types";
import { cn } from "@/shared/lib/cn";
import { formatTimeRange12h } from "@/shared/lib/time";
import { DateOverrideDialog } from "@/features/availability/ui/DateOverrideDialog";

interface DayCell {
  iso: string;
  day: number;
  inMonth: boolean;
  isToday: boolean;
  isPast: boolean;
  // The (year, monthIndex) the calendar should be on if this cell is opened
  // — for out-of-month cells, this is the neighboring month, not the
  // currently visible one. Lets the calendar follow the user's click.
  targetYear: number;
  targetMonth: number;
}

function buildMonthGrid(year: number, monthIndex: number): DayCell[] {
  const today = new Date();
  const todayIso = toIsoDate(today);
  const firstOfMonth = new Date(year, monthIndex, 1);
  const startOffset = jsDayToMondayFirst(firstOfMonth.getDay());
  const startDate = new Date(year, monthIndex, 1 - startOffset);
  const cells: DayCell[] = [];
  for (let i = 0; i < 42; i++) {
    const d = new Date(startDate);
    d.setDate(startDate.getDate() + i);
    const iso = toIsoDate(d);
    cells.push({
      iso,
      day: d.getDate(),
      inMonth: d.getMonth() === monthIndex,
      isToday: iso === todayIso,
      isPast: iso < todayIso,
      targetYear: d.getFullYear(),
      targetMonth: d.getMonth(),
    });
  }
  return cells;
}

interface DateOverrideCalendarProps {
  template: TemplateWindow[];
  exceptions: AvailabilityException[];
}

export function DateOverrideCalendar({ template, exceptions }: DateOverrideCalendarProps) {
  const today = useMemo(() => new Date(), []);
  const [year, setYear] = useState(today.getFullYear());
  const [monthIndex, setMonthIndex] = useState(today.getMonth());
  const [selectedIso, setSelectedIso] = useState<string | null>(null);

  const cells = useMemo(() => buildMonthGrid(year, monthIndex), [year, monthIndex]);
  const exMap = useMemo(() => exceptionsByDate(exceptions), [exceptions]);
  const templateByDow = useMemo(() => templateByDay(template), [template]);

  const monthLabel = new Date(year, monthIndex, 1).toLocaleDateString(undefined, {
    month: "long",
    year: "numeric",
  });

  const goPrev = () => {
    if (monthIndex === 0) {
      setYear(year - 1);
      setMonthIndex(11);
    } else {
      setMonthIndex(monthIndex - 1);
    }
  };
  const goNext = () => {
    if (monthIndex === 11) {
      setYear(year + 1);
      setMonthIndex(0);
    } else {
      setMonthIndex(monthIndex + 1);
    }
  };
  const goToday = () => {
    setYear(today.getFullYear());
    setMonthIndex(today.getMonth());
  };

  const onCellClick = (cell: DayCell) => {
    if (cell.isPast) return;
    // Snap the calendar to the cell's actual month so the user sees their
    // change land where they expect.
    if (!cell.inMonth) {
      setYear(cell.targetYear);
      setMonthIndex(cell.targetMonth);
    }
    setSelectedIso(cell.iso);
  };

  const selectedExceptions = selectedIso ? exMap.get(selectedIso) ?? [] : [];

  // The legend only matters once the calendar contains something to legend.
  const hasAnyMarker =
    exceptions.length > 0 || template.length > 0;

  const templateUninitialized = template.length === 0 && exceptions.length === 0;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={goPrev}
            className="h-8 w-8 rounded-md border border-border text-copy-muted transition-colors hover:bg-surface-sunken hover:text-copy"
            aria-label="Previous month"
          >
            ‹
          </button>
          <button
            type="button"
            onClick={goNext}
            className="h-8 w-8 rounded-md border border-border text-copy-muted transition-colors hover:bg-surface-sunken hover:text-copy"
            aria-label="Next month"
          >
            ›
          </button>
          <button
            type="button"
            onClick={goToday}
            className="ml-1 h-8 rounded-md border border-border px-3 text-xs font-medium text-copy transition-colors hover:bg-surface-sunken"
          >
            Today
          </button>
        </div>
        <span className="text-sm font-medium tracking-display-tight text-copy">{monthLabel}</span>
        {hasAnyMarker ? <Legend /> : <div className="w-[1px]" aria-hidden />}
      </div>

      <div className="relative overflow-hidden rounded-md border border-border bg-surface">
        <div className="grid grid-cols-7 border-b border-border bg-surface-sunken text-xs font-medium uppercase tracking-wider text-copy-muted">
          {DAY_LABELS.map((label) => (
            <div key={label} className="px-2 py-2 text-center">
              {label}
            </div>
          ))}
        </div>
        <div className="grid grid-cols-7">
          {cells.map((cell) => {
            const dow = jsDayToMondayFirst(
              new Date(
                parseInt(cell.iso.slice(0, 4), 10),
                parseInt(cell.iso.slice(5, 7), 10) - 1,
                parseInt(cell.iso.slice(8, 10), 10),
              ).getDay(),
            );
            const ex = exMap.get(cell.iso) ?? [];
            const isBlocked = ex.some((e) => e.kind === "BLOCKED");
            const customWindows = ex.filter((e) => e.kind === "AVAILABLE");
            const hasCustom = customWindows.length > 0;
            const tplWindows = templateByDow[dow];
            const showsTemplate = !isBlocked && !hasCustom && tplWindows.length > 0;

            return (
              <button
                key={cell.iso}
                type="button"
                disabled={cell.isPast}
                onClick={() => onCellClick(cell)}
                className={cn(
                  "flex h-20 flex-col items-stretch gap-1 border-b border-r border-border p-1.5 text-left text-xs transition-colors focus:outline-none focus-visible:relative focus-visible:z-10 focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary",
                  !cell.inMonth && "bg-surface-sunken",
                  cell.isPast && "cursor-not-allowed",
                  !cell.isPast && "hover:bg-surface-sunken",
                  cell.isToday && "bg-primary-light",
                )}
                aria-label={
                  cell.isPast
                    ? `${cell.iso} (past)`
                    : `Set availability for ${cell.iso}`
                }
              >
                <div className="flex items-center justify-between">
                  <span
                    className={cn(
                      "text-xs font-medium tabular-nums",
                      cell.isToday && "text-primary",
                      !cell.inMonth && !cell.isToday && "text-copy-subtle",
                      cell.isPast && "text-copy-subtle opacity-50",
                    )}
                  >
                    {cell.day}
                  </span>
                  {isBlocked && (
                    <span
                      className="rounded-full bg-destructive-light px-1.5 py-0.5 text-[10px] font-medium text-destructive-foreground"
                      title="Blocked"
                    >
                      Off
                    </span>
                  )}
                  {hasCustom && (
                    <span
                      className="rounded-full bg-primary-light px-1.5 py-0.5 text-[10px] font-medium text-primary"
                      title="Custom hours"
                    >
                      Custom
                    </span>
                  )}
                </div>
                {!cell.isPast && !isBlocked && hasCustom && (
                  <div className="flex flex-col gap-0.5 text-[10px] text-copy-muted">
                    {customWindows.slice(0, 2).map((w) => (
                      <span key={w.id} className="truncate">
                        {formatTimeRange12h(w.start_time, w.end_time)}
                      </span>
                    ))}
                    {customWindows.length > 2 && (
                      <span>+{customWindows.length - 2} more</span>
                    )}
                  </div>
                )}
                {!cell.isPast && showsTemplate && (
                  <div className="flex flex-col gap-0.5 text-[10px] text-copy-muted">
                    {tplWindows.slice(0, 2).map((w) => (
                      <span key={w.id} className="truncate">
                        {formatTimeRange12h(w.start_time, w.end_time)}
                      </span>
                    ))}
                    {tplWindows.length > 2 && <span>+{tplWindows.length - 2} more</span>}
                  </div>
                )}
              </button>
            );
          })}
        </div>

        {templateUninitialized && (
          <div className="pointer-events-none absolute inset-x-0 bottom-0 top-[37px] flex items-center justify-center">
            <div className="rounded-md border border-border bg-surface px-3 py-2 text-xs text-copy-muted shadow-card">
              Set a weekly schedule above, or click any future date to override it individually.
            </div>
          </div>
        )}
      </div>

      {selectedIso && (
        <DateOverrideDialog
          open={selectedIso !== null}
          onOpenChange={(o) => !o && setSelectedIso(null)}
          date={selectedIso}
          exceptions={selectedExceptions}
          template={template}
        />
      )}
    </div>
  );
}

function Legend() {
  return (
    <div className="hidden items-center gap-2 md:flex">
      <span className="rounded-full bg-primary-light px-2 py-0.5 text-[10px] font-medium text-primary">
        Custom
      </span>
      <span className="rounded-full bg-destructive-light px-2 py-0.5 text-[10px] font-medium text-destructive-foreground">
        Off
      </span>
      <span className="rounded-full border border-border px-2 py-0.5 text-[10px] font-medium text-copy-muted">
        Template
      </span>
    </div>
  );
}
